module.exports = (() => {
    const WVKit = {}

    const isServer = typeof window === "undefined"
    const isObject = (o) => o === Object(o) && !Array.isArray(o) && typeof o !== "function"

    const methodNameConstants = {
        ON_MESSAGE: "dataLayerOnMessage",
        POST_MESSAGE: "dataLayerPostMessage",
    }

    // Checks if webview is invoked from Android platform
    WVKit.isAndroid = () => {
        return (!isServer && window.AppCallbacks) || false
    }

    // Checks if webview is invoked from IOS
    WVKit.isIOS = () => {
        return (!isServer && window.webkit && window.webkit.messageHandlers) || false
    }

    /**
     * @description Logs and constructs webview based error messages
     * @param {String} args Expects single/multiple string based arguments
     */
    const logger = (...args) => {
        const log = ["WebView Error!!", ...args].join("\n")
        //transformed to new log format
        console.dir({
            service_name: "pwa-mweb-node-server",
            loglevel: "info",
            version: "v2",
            message: `\n \n ====> ${log}`,
        })
    }

    /**
     * @description try-catch wrapper for the callback method being passed
     * @param {Function} cb
     */
    const validate = (cb) => {
        try {
            return cb()
        } catch (error) {
            logger(`Error Stack: ${error.stack}`)
        }
    }

    /**
     * @description Constructs arguments for the callback handler methods
     * @param {String} methodName
     * @param {Object} info
     * @returns {Array} arguments array
     */
    const constructArguments = (methodName, info) =>
        validate(() => {
            const args = []
            const infoExists = isObject(info)
            // Args (android) - [methodName, stringified object]
            // Args (ios) - [stringified object]
            if (WVKit.isAndroid() && methodName === methodNameConstants.POST_MESSAGE) {
                if (infoExists) {
                    args.push((info && info.methodName) || "")
                    args.push(JSON.stringify((info && info.info) || {}))
                }
            } else if (infoExists) {
                args.push(JSON.stringify(info))
            } else {
                args.push(info)
            }
            return args
        })

    // Returns true, if platform is Android or IOS, and min version requirement is satisfied
    WVKit.isValidPlatform = () => {
        return !isServer && (WVKit.isAndroid() || WVKit.isIOS())
    }

    /**
     * @description Parses stringified JSON data
     * @param {String} info Stringified JSON data
     * @returns Parsed JS Object
     */
    WVKit.parser = (info) =>
        validate(() => {
            let data = info
            if (WVKit.isAndroid()) data = data.replace(/\\/g, "")
            data = JSON.parse(data)
            return data
        })

    // Gets platfrom prefix for Andriod and IOS
    WVKit.getPlatformPrefix = () => {
        const androidPrefix = WVKit.isAndroid()
        const iosPrefix = WVKit.isIOS()
        return androidPrefix || iosPrefix
    }

    /**
     * @description Handler for webview based callbacks (webview -> native)
     * @param {String} methodName
     * @param {Object} info
     * @returns {*}
     */
    WVKit.call = (methodName = "", info = null) =>
        validate(() => {
            if (WVKit.isValidPlatform()) {
                const platformPrefix = WVKit.getPlatformPrefix()
                if (methodName) {
                    const args = constructArguments(methodName, info)
                    if (WVKit.isAndroid() && typeof platformPrefix[methodName] === "function") {
                        return platformPrefix[methodName](...args)
                    } else if (
                        WVKit.isIOS() &&
                        platformPrefix[methodName] &&
                        typeof platformPrefix[methodName].postMessage === "function"
                    ) {
                        return platformPrefix[methodName].postMessage(...args)
                    } else {
                        logger(`Failed to call method - ${methodName}`)
                    }
                }
            } else {
                logger("This platform is not supported")
            }
        })

    /**
     * @description Constructs injectable script containing webview based callbacks
     * @param {String} methodName
     * @param {String} info
     * @returns
     */
    WVKit.inject = (methodName = "", info = null, nonce = "") => {
        return `
        <script nonce=${nonce}>
            const androidPlatformPrefix = window.AppCallbacks;
            const iosPlatformPrefix = window.webkit && window.webkit.messageHandlers;
            if (androidPlatformPrefix &&
                typeof androidPlatformPrefix.${methodName} === "function") {
                try {
                    androidPlatformPrefix.${methodName}(${info});
                } catch (error) {
                    console.log("WebView Error!!", error.message);
                }
            }
            if (iosPlatformPrefix && iosPlatformPrefix.${methodName} &&
                typeof iosPlatformPrefix.${methodName}.postMessage === "function") {
                try {
                    iosPlatformPrefix.${methodName}.postMessage(${info});
                } catch (error) {
                    console.log("WebView Error!!", error.message);
                }
            }
        </script>
    `
    }

    const ANDROID_MIN_SUPPORTED_VERSION = "Android-14.1.0"
    const IOS_MIN_SUPPORTED_VERSION = "iOS-13.7.0"

    const ANDROID_FEATURES_SUPPORT_VERSION = {
        IMAGE_UPLOAD: "Android-15.4.0",
    }

    const IOS_FEATURES_SUPPORT_VERSION = {}

    /**
     * @description Compares current native version against minimum supported version
     * @param {String} currVersion
     * @param {String} minVersion
     * @returns {Boolean}
     */
    const minVersionCheck = (currVersion, minVersion) => {
        const getVersion = (value) => {
            return value.split("-")[1].split(".")
        }
        const version = getVersion(currVersion)
        const minSupportedVersion = getVersion(minVersion)
        if (version[0] >= minSupportedVersion[0]) {
            if (version[0] > minSupportedVersion[0]) return true
            else if (version[1] >= minSupportedVersion[1]) {
                if (version[1] > minSupportedVersion[1]) return true
                else if (version[2] >= minSupportedVersion[2]) {
                    return true
                }
            }
        }
        return false
    }

    /**
     * @description Checks minimum version requirements for native platforms
     * @param {String} header
     * @param {String} platform
     * @returns {Boolean}
     */
    WVKit.versionCheck = (header = "", platform = "") =>
        validate(() => {
            if (platform === "Android") {
                return minVersionCheck(header, ANDROID_MIN_SUPPORTED_VERSION)
            } else if (platform === "iOS") {
                return minVersionCheck(header, IOS_MIN_SUPPORTED_VERSION)
            } else {
                return false
            }
        })

    /**
     * @description Checks minimum version support for native features
     * @param {String} header
     * @param {String} platform
     * @param {String} supportFeature
     * @returns {Boolean}
     */
    WVKit.featureSupport = (header = "", platform = "", supportFeature = "") =>
        validate(() => {
            let minSupportVersion = ""
            const feature = String(supportFeature).toUpperCase()
            if (platform === "Android") {
                minSupportVersion = ANDROID_FEATURES_SUPPORT_VERSION[feature]
            } else if (platform === "iOS") {
                minSupportVersion = IOS_FEATURES_SUPPORT_VERSION[feature]
            }
            return minSupportVersion ? minVersionCheck(header, minSupportVersion) : false
        })

    return WVKit
})()
